/**
 * 67.50 (3), (2)'s far half (the owner: "ridgelines that you can see through at low res upres taller and block your
 * vision when you get close"): the far ground drawn nearer the fine relief's crest, as a dial (the Lab's Far ridges row,
 * `?farlift=`). The sim's ground is the coarse surface plus the relief (core/fineTerrain.ts: 0 to the amplitude A); drawn
 * at the coarse surface, a far ridge stands under the sim's. 71.3 (a) (DECISIONS 324, ARCHITECTURE §5.36 (4a)): by default
 * the relief's mean (FAR_LIFT_MEAN, 0.5 A — 0.4997 measured, its spread 0.15 A), so ground coming into the patch's band
 * moves by about ±0.15 A, not 0 → A, and the far ground stops lying under the near one (it was 0, the ground as #934 left it).
 *
 * **The lift is the patch's fade turned over**: L × (1 − fade²) — none where the fine patch draws the whole relief,
 * all of it where the patch has faded out, nothing while no patch is drawn. The patch rides the same term (its vertex:
 * coarse + L(1 − fade²) + relief × mask × fade²), so the planet's mesh is never above the patch — the waist-deep fault
 * #934 turned the lift off for (#931 lifted the mesh by all of L under the patch too, and the mesh hid the ground people
 * stood on) — and near the camera the ground, the props and the people are the sim's exactly (tests/groundLift.test.ts:
 * the drawn heights compared). Lifted by it: the planets' meshes (`applyGroundLift`), the patch, the scatter and the
 * trees' tufts (their shaders); drawnGround mirrors it. Not lifted: the props and everything placed once on the CPU
 * (decals, debris, fauna, drop cans) — drawn where the sim has them, they sink into the far ground by the lift. People
 * stand on the drawn ground (never drawn buried); far off they are drawn up to the lift above their bodies.
 *
 * The cost of a lift, measured (sweeps/2026-10-11-crest-lift-2.md): past ~45 people a person stands off his body by
 * p50 0.3, p90 0.54 people at the crest (0.75 A), p90 0.24 at half of it. Presentation only.
 */
import * as THREE from 'three';
import type { PersonSet } from '../core/scaleProfile';

/** The relief's crest as a share of its amplitude (sweeps/2026-10-11-crest-lift.md: a far ridge hides what the sim hides). */
import type { PlanetState, Vec3 } from '../types';
import { fineOffset, digHeightAt, craterletHeightAt } from '../core/fineTerrain';
import { terraceGround } from '../core/terraceGround';

/**
 * 69.24: the fine ground's lift over the coarse surface `coarse` at `d` (world), as the sim's ground has it: the relief, its
 * bowls (a dig's, a craterlet's — 63.11) and its terraces — what the patch's vertices take and what a piece of scatter or a
 * tree's tuft must ride (they took the relief alone, and hung over a burst's bowl). Without fine ground, 0.
 */
export function fineGroundLift(planet: PlanetState, d: Vec3, ps: { fineAmplitude: number; fineWavelength: number }, k: number, coarse: number): number {
  if (!(ps.fineAmplitude > 0)) return 0;
  const dug = (planet.digs?.length ? digHeightAt(planet, d.x, d.y, d.z, k) : 0) + (planet.craterlets?.length ? craterletHeightAt(planet, d.x, d.y, d.z, k) : 0);
  const relief = fineOffset(planet, d.x, d.y, d.z, ps.fineAmplitude, ps.fineWavelength) + dug;
  return planet.terraces?.length ? terraceGround(planet.terraces, planet.radius, d.x, d.y, d.z, coarse + relief) - coarse : relief;
}

export const FAR_LIFT_CREST = 0.75;
/** 71.3 (a): the relief's mean, a share of its amplitude (fineOffset: 0.8 and 0.2 of two smoothed lattice noises in 0 … 1) — the default. */
export const FAR_LIFT_MEAN = 0.5;
/** The Far ridges dial's choices (shares of the amplitude): off, the relief's mean (the default), its crest. */
export const FAR_LIFT_SHARES = [0, FAR_LIFT_MEAN, FAR_LIFT_CREST] as const;

let shareOverride: number | null = null;
/** The Lab's launch (null: back to the URL / the default). */
export function setFarLiftShare(v: number | null): void { shareOverride = v; }
/** The far lift's share in force: the Lab's override, else the page's `?farlift=` (0 … 1; 0 is off), else the relief's mean (71.3 (a)). */
export function farLiftShare(): number {
  if (shareOverride !== null) return shareOverride;
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('farlift') : null;
    const v = q === null || q === '' ? NaN : Number(q);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : FAR_LIFT_MEAN;
  } catch { return FAR_LIFT_MEAN; }
}

/** The shared uniform the lifted shaders read (the planets', the scatter's and the tufts'). */
export const GROUND_LIFT = { uGroundLift: { value: 0 } };

/**
 * The fine patch's fade in world space (rendering/fineTerrainPatch.ts writes it each frame): the focus's ground point,
 * the fade's distances, and on (1) while a patch is drawn — off (0) with none, so nothing lifts. The scatter's and the
 * tufts' shaders fade their relief by it, and every lifted shader ramps its lift past it.
 */
export const FINE_FADE = { uFineFocus: { value: new THREE.Vector3() }, uFineFade: { value: new THREE.Vector2(0, 1) }, uFineOn: { value: 0 } };

/**
 * GLSL: the far lift at a world point, as every lifted shader takes it (`farLiftAt(wp)`; meshLift mirrors it): the
 * lift × on × (1 − the patch's fade²). Declares its uniforms; goes after `#include <common>`.
 */
export const FAR_LIFT_GLSL = `uniform float uGroundLift;
uniform vec3 uFineFocus;
uniform vec2 uFineFade;
uniform float uFineOn;
float farLiftAt(vec3 wp) { float f = uFineOn * (1.0 - smoothstep(uFineFade.x, uFineFade.y, distance(wp, uFineFocus))); return uGroundLift * uFineOn * (1.0 - f * f); }`;   // (f × on: the patch's height fade, as its own vertices take it)

/**
 * 69.24 (the owner: "the rough terrain snaps above the fine terrain … usually in a place where some explosions have happened"):
 * how far the planet's mesh sinks under the fine patch (people), × the patch's fade² — a bowl in the fine ground, a craterlet
 * (63.11: 0.12–0.22 people deep) or a dig (66.8: a foxhole's floor 0.67), goes below the coarse surface where the relief there is
 * shallower, and the patch drew its vertex under the mesh, which won the depth test: the coarse ground showed through. The
 * deepest bowl and a little: the patch is never under the mesh. The mesh's only (the scatter's farLiftAt is the lift alone).
 */
export const BOWL_SINK_PEOPLE = 0.75;
export const GROUND_SINK = { uGroundSink: { value: 0 } };
export function setGroundSink(v: number): void { GROUND_SINK.uGroundSink.value = v > 0 ? v : 0; }

/** GLSL: the mesh's sink at a world point (`meshSinkAt(wp)`, after FAR_LIFT_GLSL): the sink × the patch's fade² (meshLift mirrors it). */
const MESH_SINK_GLSL = `uniform float uGroundSink;
float meshSinkAt(vec3 wp) { float f = uFineOn * (1.0 - smoothstep(uFineFade.x, uFineFade.y, distance(wp, uFineFocus))); return uGroundSink * f * f; }`;

/** The planet's mesh's lift at a world point now (world units): the far lift less the bowls' sink — its shader on the CPU (drawnGround, the tests). */
export function meshLift(p: { x: number; y: number; z: number }): number {
  const on = FINE_FADE.uFineOn.value, L = GROUND_LIFT.uGroundLift.value, S = GROUND_SINK.uGroundSink.value;
  if (!(on > 0) || !(L > 0 || S > 0)) return 0;
  const fo = FINE_FADE.uFineFocus.value, inner = FINE_FADE.uFineFade.value.x, outer = FINE_FADE.uFineFade.value.y;
  const dx = p.x - fo.x, dy = p.y - fo.y, dz = p.z - fo.z, d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const t = Math.min(1, Math.max(0, (d - inner) / (outer - inner))), f = on * (1 - t * t * (3 - 2 * t));   // (× on: the height fade)
  return L * on * (1 - f * f) - S * f * f;
}

/** The far ground's lift now (world units): what the CPU's placements add to the coarse surface. */
export function groundLift(): number { return GROUND_LIFT.uGroundLift.value; }
export function setGroundLift(v: number): void { GROUND_LIFT.uGroundLift.value = v > 0 ? v : 0; }

/** The lift a battle with this person set draws (the dial's share of its relief; 0 at classic, where there is none). */
export function farLift(ps: Pick<PersonSet, 'fineAmplitude'>): number { return ps.fineAmplitude > 0 ? farLiftShare() * ps.fineAmplitude : 0; }

/** A planet mesh's material: each vertex lifted along its radial by the far lift where it is (the mesh's origin is the planet's centre). */
export function applyGroundLift(material: THREE.Material): void {
  const before = material.onBeforeCompile?.bind(material);
  material.onBeforeCompile = (shader, renderer) => {
    before?.(shader, renderer);
    Object.assign(shader.uniforms, GROUND_LIFT, FINE_FADE, GROUND_SINK);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${FAR_LIFT_GLSL}\n${MESH_SINK_GLSL}`)
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vec3 wpLift = (modelMatrix * vec4(position, 1.0)).xyz;\n  transformed += normalize(position) * (farLiftAt(wpLift) - meshSinkAt(wpLift));');   // (69.24: under the patch, sunk below its bowls)
  };
  const key = material.customProgramCacheKey?.bind(material);
  material.customProgramCacheKey = () => `${key ? key() : ''}|ground-lift-sink`;
  material.needsUpdate = true;
}
